import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { map } from 'lodash';

import BlockSection from '~/components/wrappers/BlockSection';

import ButtonGroup from '~/components/base/ButtonGroup';
import Image from '~/components/base/Image';

import UserContent from '~/components/UserContent';
import IconList from '~/components/IconList';

import theme from '~/theme';
import { h2, eyebrow } from '~/styles/typography';
import { calcVw } from '~/styles/helpers';
import { formatText } from '~/utilities/helpers';
import { setSmallSpacing } from '~/utilities/spacing';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes,
    AcfLink
} from '~/utilities/propTypes';

export const customGrid = css`
    margin-left: calc((${calcVw(100)} - 100%) / -2);
    margin-right: calc((${calcVw(100)} - 100%) / -2);
    display: grid;
    grid-template-columns: ${theme.layout.gutter} 1fr ${theme.layout.gutter};

    @media (min-width: ${theme.breakpoint.desktop}) {
        grid-template-columns:
            1fr ${theme.layout.largeGutter} minmax(
                0,
                calc(
                    (${theme.layout.containerWidth} / 2) -
                        ${theme.layout.largeGutter} -
                        (${theme.layout.gutter} / 2)
                )
            )
            ${theme.layout.gutter} minmax(
                0,
                calc(
                    (${theme.layout.containerWidth} / 2) -
                        ${theme.layout.largeGutter} -
                        (${theme.layout.gutter} / 2)
                )
            )
            ${theme.layout.largeGutter} 1fr;
    }

    @media (min-width: ${theme.breakpoint.largeDesktop}) {
        grid-template-columns:
            1fr ${theme.layout.largeGutter} minmax(
                0,
                calc(
                    (${theme.layout.containerWidthLg} / 2) -
                        ${theme.layout.largeGutter} -
                        (${theme.layout.gutter} / 2)
                )
            )
            ${theme.layout.gutter} minmax(
                0,
                calc(
                    (${theme.layout.containerWidthLg} / 2) -
                        ${theme.layout.largeGutter} -
                        (${theme.layout.gutter} / 2)
                )
            )
            ${theme.layout.largeGutter} 1fr;
    }
`;

const Grid = styled.div`
    ${customGrid}
    row-gap: 30px;
    grid-template-areas:
        'image image image'
        '. content .';
    align-items: center;

    @media (min-width: ${theme.breakpoint.desktop}) {
        grid-template-areas: ${({ imageRight }) =>
            imageRight
                ? `". . content . image image image"`
                : `"image image image . content . ."`};
    }

    @media (min-width: ${theme.breakpoint.largeDesktop}) {
        grid-template-areas: ${({ imageRight }) =>
            imageRight
                ? `". . content . image . ."`
                : `". . image . content . ."`};
    }
`;

const SideBleedImage = styled(Image)`
    grid-area: image;
`;

const ContentWrapper = styled.div`
    grid-area: content;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    ${setSmallSpacing('gap')}
`;

const TopContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Title = styled.h2`
    ${h2}
    color: ${theme.colors.text.red};
`;

const Category = styled.span`
    ${eyebrow};
`;

const FullWidthFeature = ({
    image,
    imageRight = false,
    title = '',
    category = '',
    copy = '',
    features = [],
    ctas = [],
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => (
    <BlockSection {...{ globalBlockOptions, attributes }} padding="none">
        <Grid {...{ imageRight }}>
            <SideBleedImage {...image} />
            <ContentWrapper>
                {category ? <Category>{formatText(category)}</Category> : null}
                {title || copy ? (
                    <TopContent>
                        {title ? <Title>{formatText(title)}</Title> : null}
                        {copy ? <UserContent content={copy} /> : null}
                    </TopContent>
                ) : null}

                {features?.length ? <IconList {...{ features }} /> : null}

                {ctas?.length ? (
                    <ButtonGroup
                        links={map(
                            ctas,
                            ({
                                link,
                                useComparisonTable,
                                comparisonTableProductCategory
                            }) => ({
                                ...link,
                                useComparisonTable,
                                comparisonTableProductCategory:
                                    comparisonTableProductCategory?.id
                            })
                        )}
                    />
                ) : null}
            </ContentWrapper>
        </Grid>
    </BlockSection>
);

FullWidthFeature.propTypes = {
    imageRight: PropTypes.bool,
    image: PropTypes.shape({}).isRequired,
    title: PropTypes.string,
    category: PropTypes.string,
    copy: PropTypes.string,
    features: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string,
            feature: PropTypes.string.isRequired
        })
    ),
    ctas: PropTypes.arrayOf(
        PropTypes.shape({
            link: AcfLink,
            useComparisonTable: PropTypes.bool,
            comparisonTableProductCategory: PropTypes.shape({
                id: PropTypes.string
            })
        })
    ),
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default FullWidthFeature;
